import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const INDUSTRIES = [
  'Coaching & Consulting',
  'E-commerce & Retail',
  'Education & Courses',
  'Finance & Investments',
  'Health & Wellness',
  'Marketing & Advertising',
  'Real Estate',
  'SaaS & Technology',
  'Other'
];

const USAGE_TYPES = [
  { value: 'personal', label: 'Personal Use', description: 'For my own business planning' },
  { value: 'team', label: 'Team Use', description: 'For our company/team' },
  { value: 'agency', label: 'Agency Use', description: 'For client work and consulting' }
];

const PRIMARY_GOALS = [
  'Calculate revenue targets',
  'Plan marketing budgets',
  'Optimize conversion rates',
  'Compare offer strategies',
  'Present to stakeholders'
];

export default function Onboarding() {
  const { completeOnboarding, user } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    industry: '',
    usage_type: '',
    primary_goal: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!formData.industry || !formData.usage_type || !formData.primary_goal) {
      setError('Please complete all steps');
      return;
    }

    setLoading(true);
    const result = await completeOnboarding(formData);
    
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  const nextStep = () => {
    if (step === 1 && !formData.industry) {
      setError('Please select your industry');
      return;
    }
    if (step === 2 && !formData.usage_type) {
      setError('Please select how you plan to use this');
      return;
    }
    setError('');
    setStep(step + 1);
  };

  const prevStep = () => {
    setError('');
    setStep(step - 1);
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      {/* Background gradient effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-[#FF7901]/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-[#DF1283]/30 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#4A0A66]/10 rounded-full blur-[150px]" />
      </div>

      <div className="relative w-full max-w-lg">
        {/* Welcome Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-black text-white mb-3">
            Welcome, <span className="text-[#0FFFE2]">{user?.first_name || 'there'}!</span>
          </h1>
          <p className="text-gray-300 text-lg font-medium">Let's personalize your experience</p>
          
          {/* Progress indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-2 rounded-full transition-all duration-300 ${
                  s === step 
                    ? 'w-8 bg-gradient-to-r from-[#DF1283] to-[#FF7901]' 
                    : s < step 
                      ? 'w-8 bg-[#0FFFE2]' 
                      : 'w-2 bg-[#4A0A66]'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Card with light pink glow */}
        <div className="relative">
          {/* Subtle outer glow */}
          <div className="absolute -inset-3 bg-[#DF1283] rounded-3xl opacity-20 blur-2xl" />
          <div className="absolute -inset-1 bg-[#DF1283] rounded-2xl opacity-30 blur-lg" />
          
          {/* Border */}
          <div className="absolute -inset-0.5 bg-[#DF1283] rounded-2xl opacity-40" />
          
          <div className="relative bg-black/95 backdrop-blur-xl rounded-2xl p-8 border border-[#DF1283]/20">
            {error && (
              <div className="bg-[#DF1283]/10 border border-[#DF1283]/50 rounded-lg px-4 py-3 text-[#DF1283] text-sm mb-6">
                {error}
              </div>
            )}

            {/* Step 1: Industry */}
            {step === 1 && (
              <div className="space-y-4" data-testid="onboarding-step-1">
                <h2 className="text-2xl font-bold text-white mb-5 text-center">What Industry Are You In?</h2>
                <div className="grid grid-cols-2 gap-3">
                  {INDUSTRIES.map((industry) => (
                    <button
                      key={industry}
                      onClick={() => setFormData({ ...formData, industry })}
                      data-testid={`industry-${industry.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`px-4 py-3 rounded-lg text-left text-sm font-semibold transition-all duration-200 ${
                        formData.industry === industry
                          ? 'bg-gradient-to-r from-[#DF1283] to-[#FF7901] text-white shadow-lg shadow-[#DF1283]/25'
                          : 'bg-[#0a0014] border border-[#4A0A66]/50 text-gray-300 hover:border-[#DF1283]/50'
                      }`}
                    >
                      {industry}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Usage Type */}
            {step === 2 && (
              <div className="space-y-4" data-testid="onboarding-step-2">
                <h2 className="text-2xl font-bold text-white mb-5 text-center">How Do You Plan To Use The UnCloned® Offer Calculator?</h2>
                <div className="space-y-3">
                  {USAGE_TYPES.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setFormData({ ...formData, usage_type: type.value })}
                      data-testid={`usage-${type.value}`}
                      className={`w-full px-4 py-4 rounded-lg text-left transition-all duration-200 ${
                        formData.usage_type === type.value
                          ? 'bg-gradient-to-r from-[#0FFFE2]/20 to-[#D0FF0B]/20 border-2 border-[#0FFFE2]'
                          : 'bg-[#0a0014] border border-[#4A0A66]/50 hover:border-[#0FFFE2]/50'
                      }`}
                    >
                      <div className="font-bold text-white text-lg">{type.label}</div>
                      <div className="text-sm text-gray-300 mt-1 font-medium">{type.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Primary Goal */}
            {step === 3 && (
              <div className="space-y-4" data-testid="onboarding-step-3">
                <h2 className="text-2xl font-bold text-white mb-5 text-center">What's Your Primary Goal?</h2>
                <div className="space-y-3">
                  {PRIMARY_GOALS.map((goal) => (
                    <button
                      key={goal}
                      onClick={() => setFormData({ ...formData, primary_goal: goal })}
                      data-testid={`goal-${goal.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`w-full px-4 py-3 rounded-lg text-left font-bold text-base transition-all duration-200 ${
                        formData.primary_goal === goal
                          ? 'bg-gradient-to-r from-[#D0FF0B] to-[#0FFFE2] text-black shadow-lg shadow-[#D0FF0B]/25'
                          : 'bg-[#0a0014] border border-[#4A0A66]/50 text-gray-300 hover:border-[#D0FF0B]/50'
                      }`}
                    >
                      {goal}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8">
              {step > 1 ? (
                <button
                  onClick={prevStep}
                  data-testid="onboarding-back"
                  className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
                >
                  Back
                </button>
              ) : (
                <div />
              )}

              {step < 3 ? (
                <button
                  onClick={nextStep}
                  data-testid="onboarding-next"
                  className="px-6 py-2 bg-gradient-to-r from-[#DF1283] to-[#FF7901] text-white font-semibold rounded-lg hover:opacity-90 transition-all"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  data-testid="onboarding-complete"
                  className="px-6 py-2 bg-gradient-to-r from-[#D0FF0B] to-[#0FFFE2] text-black font-semibold rounded-lg hover:opacity-90 transition-all disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Get Started'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-white/40 mt-8">
          <p>© 2025-2026 UnCloned® Media. All Rights Reserved.</p>
        </div>
      </div>
    </div>
  );
}
